import React from 'react';
import {
  Wrench,
  GraduationCap,
  Briefcase,
  Building2,
  Code2,
  Globe
} from 'lucide-react';
import { InfoPopover } from './common';

interface BotPurpose {
  id: string;
  name: string;
  icon: React.ReactNode;
  description: string;
  examples: string[];
  isRecommended?: boolean;
  rawBot?: any;
}

interface BotPurposeSelectorProps {
  selectedPurpose: string;
  onPurposeChange: (purpose: string) => void;
  disabled?: boolean;
  llmRecommendations?: any[];  // Optional LLM bot type recommendations
  recommendedType?: string;     // Optional recommended bot type
}

const botPurposes: BotPurpose[] = [
  {
    id: 'support',
    name: 'Support Bot',
    icon: <Wrench className="w-6 h-6" />,
    description: 'Customer support, troubleshooting & help content',
    examples: ['FAQ pages', 'Help articles', 'Troubleshooting guides', 'Contact info']
  },
  {
    id: 'educational',
    name: 'Educational Bot',
    icon: <GraduationCap className="w-6 h-6" />,
    description: 'Teaching materials, tutorials & learning resources',
    examples: ['Tutorials', 'How-to guides', 'Course materials', 'Documentation']
  },
  {
    id: 'sales',
    name: 'Sales Assistant',
    icon: <Briefcase className="w-6 h-6" />,
    description: 'Product info, pricing & sales materials',
    examples: ['Product features', 'Pricing pages', 'Case studies', 'Testimonials']
  },
  {
    id: 'company',
    name: 'Company Info Bot',
    icon: <Building2 className="w-6 h-6" />,
    description: 'About the company, team & culture',
    examples: ['About us', 'Team page', 'Company news', 'Careers']
  },
  {
    id: 'technical',
    name: 'Technical Docs Bot',
    icon: <Code2 className="w-6 h-6" />,
    description: 'API docs, developer guides & technical specs',
    examples: ['API docs', 'Integration guides', 'Code examples', 'Technical specs']
  },
  {
    id: 'general',
    name: 'General Knowledge Bot',
    icon: <Globe className="w-6 h-6" />,
    description: 'All website content comprehensively',
    examples: ['All content', 'Comprehensive crawl']
  }
];

const BotPurposeSelector: React.FC<BotPurposeSelectorProps> = ({
  selectedPurpose,
  onPurposeChange,
  disabled = false,
  llmRecommendations,
  recommendedType
}) => {
  const hasLLMRecommendations = !!(llmRecommendations && llmRecommendations.length > 0);
  // Use LLM recommendations if available, otherwise use default purposes
  const displayPurposes = React.useMemo(() => {
    console.log('[BotPurposeSelector] llmRecommendations:', llmRecommendations);
    console.log('[BotPurposeSelector] recommendedType:', recommendedType);
    console.log('[BotPurposeSelector] llmRecommendations length:', llmRecommendations?.length);

    if (llmRecommendations && llmRecommendations.length > 0) {
      // Map LLM recommendations to BotPurpose format
      const mapped = llmRecommendations.map((bot: any, index: number) => {
        console.log(`[BotPurposeSelector] Processing bot ${index}:`, bot);
        console.log(`[BotPurposeSelector] Bot ${index} total_urls:`, bot.total_urls);

        const processedBot: BotPurpose = {
          id: bot.bot_type.toLowerCase().replace(/ /g, '-'),
          name: bot.bot_type,
          icon: <span className="text-2xl">{bot.icon}</span>,
          description: bot.description || bot.reason,
          examples: [`${bot.total_urls || 0} pages`, `${bot.score || 0}% match`, ...(bot.category_names || []).slice(0, 2)],
          isRecommended: bot.bot_type === recommendedType,
          rawBot: bot
        };

        console.log(`[BotPurposeSelector] Processed bot ${index}:`, processedBot);
        return processedBot;
      });
      console.log('[BotPurposeSelector] Final mapped purposes:', mapped);
      return mapped;
    }
    console.log('[BotPurposeSelector] Using default purposes');
    return botPurposes;
  }, [llmRecommendations, recommendedType]);

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm text-gray-600 mb-4">
          {llmRecommendations && llmRecommendations.length > 0
            ? '✨ AI-recommended bot types based on your website content:'
            : 'Select bot type to crawl relevant content'}
        </p>
        {llmRecommendations && llmRecommendations.length > 0 && (
          <div className="mb-4 p-3 bg-green-50 rounded-lg">
            <p className="text-sm text-green-800">
              ✅ Found {llmRecommendations.length} bot type recommendations
            </p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {displayPurposes.length === 0 ? (
          <div className="col-span-full text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
            <p className="text-gray-500">Loading bot recommendations...</p>
            {llmRecommendations && (
              <div className="mt-4 p-3 bg-red-50 rounded text-xs text-red-800">
                <p>Debug: llmRecommendations exists but length is 0</p>
                <p>Raw data: {JSON.stringify(llmRecommendations)}</p>
              </div>
            )}
          </div>
        ) : (
          displayPurposes.map((purpose: BotPurpose) => {
            const botCategories = Array.isArray(purpose.rawBot?.categories) ? purpose.rawBot.categories : [];
            const hasTooltip = hasLLMRecommendations && purpose.rawBot && botCategories.length > 0;

            const buttonContent = (
              <button
                onClick={() => onPurposeChange(purpose.id)}
                disabled={disabled}
                className={`
                  relative w-full p-4 rounded-lg border-2 transition-all duration-200
                  ${selectedPurpose === purpose.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                  }
                  ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                  text-left outline-none focus:ring-2 focus:ring-blue-400
                `}
              >
                {/* Selection indicator */}
                {selectedPurpose === purpose.id && (
                  <div className="absolute top-2 right-2">
                    <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>
                )}

                <div className="flex items-start space-x-3">
                  <div className={`
                    flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center
                    ${selectedPurpose === purpose.id ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}
                  `}>
                    {purpose.icon}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-gray-900 truncate">
                        {purpose.name}
                      </h4>
                      {purpose.isRecommended && (
                        <div className="relative group flex-shrink-0">
                          <div className="w-5 h-5 bg-gradient-to-br from-yellow-400 to-amber-500 rounded flex items-center justify-center shadow-sm cursor-help">
                            <span className="text-white text-xs font-bold">★</span>
                          </div>
                          <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                            Recommended
                            <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                          </div>
                        </div>
                      )}
                      {hasTooltip && (
                        <div className="flex-shrink-0">
                          <InfoPopover
                            title={`Categories in ${purpose.name}`}
                            categories={botCategories}
                            totalPages={purpose.rawBot?.total_urls}
                            score={purpose.rawBot?.score}
                            position="top"
                          />
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                      {purpose.description}
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {purpose.examples.slice(0, 2).map((example: string, idx: number) => (
                        <span
                          key={idx}
                          className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded"
                        >
                          {example}
                        </span>
                      ))}
                      {purpose.examples.length > 2 && (
                        <span className="text-xs text-gray-500">
                          +{purpose.examples.length - 2} more
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            );

            return (
              <div key={purpose.id}>
                {buttonContent}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default BotPurposeSelector;
